import React, { useState, useEffect } from 'react';
import { Card } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Button } from "@shared/ui/button";
import { Bell, Globe, CheckCircle2, Save } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { useConsumerDetail, useUpdateConsumer } from '../../../hooks';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';

export const PreferencesLanding = () => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const { mutate: updateConsumer, isPending } = useUpdateConsumer();
  const { consumerId, remoteUtilityId, remoteConsumerNumber } = getLoginDataFromStorage();
  
  const { data: consumerDetail, refetch } = useConsumerDetail(
    { remote_utility_id: Number(remoteUtilityId), consumer_no: remoteConsumerNumber },
    { enabled: !!remoteUtilityId && !!remoteConsumerNumber }
  );
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(true);
  const [push, setPush] = useState(false);
  const [paperless, setPaperless] = useState(true);
  
  const [language, setLanguage] = useState(i18n.language || 'en');

  useEffect(() => {
    if (consumerDetail?.result) {
      const data = consumerDetail.result;
      
      if (data.isPaperedBill !== undefined) {
        setPaperless(!data.isPaperedBill);
      }
      
      if (data.additionalData?.communication) {
        const comm = data.additionalData.communication;
        setEmail(comm.includes('email'));
        setSms(comm.includes('phone'));
        setPush(comm.includes('push'));
      }
      
      if (data.additionalData?.language) {
        const lang = data.additionalData.language;
        setLanguage(lang);
        i18n.changeLanguage(lang);
        localStorage.setItem('appLanguage', lang);
      }
    }
  }, [consumerDetail, i18n]);

  const handleSave = () => {
    localStorage.setItem('appLanguage', language);
    i18n.changeLanguage(language);
    
    const communication = [];
    if (email) communication.push('email');
    if (sms) communication.push('phone');
    if (push) communication.push('push');

    const payload = {
      is_papered_bill: !paperless,
      additional_data: {
        communication,
        language
      }
    };

    updateConsumer(
      { consumerId, payload },
      {
        onSuccess: () => {
          refetch();
          toast({
            title: t("Preferences Saved"),
            description: t("Your preferences have been updated successfully."),
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update preferences.",
            variant: "destructive",
          });
        }
      }
    );
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-3xl pb-10">
      {/* Notification Preferences */}
      <Card className="border border-slate-200 shadow-sm rounded-2xl p-6 md:p-8 bg-white">
        <div className="flex items-center gap-2.5 mb-6 text-slate-800">
          <Bell className="w-[18px] h-[18px] text-slate-500" />
          <h3 className="text-[15px] font-bold">{t("Notification Preferences")}</h3>
        </div>

        <div className="flex flex-col">
          {/* Email */}
          <div className="flex items-center justify-between py-5 border-b border-slate-100 last:border-0 first:pt-0">
            <div>
              <div className="text-[13px] font-semibold text-slate-800 mb-0.5">{t("Email Notifications")}</div>
              <div className="text-xs text-slate-500">{t("Bill reminders, complaint updates via email")}</div>
            </div>
            <Switch checked={email} onCheckedChange={setEmail} className="data-[state=checked]:bg-emerald-600" />
          </div>
          
          {/* SMS */}
          <div className="flex items-center justify-between py-5 border-b border-slate-100 last:border-0">
            <div>
              <div className="text-[13px] font-semibold text-slate-800 mb-0.5">{t("SMS Notifications")}</div>
              <div className="text-xs text-slate-500">{t("Text alerts for urgent items")}</div>
            </div>
            <Switch checked={sms} onCheckedChange={setSms} className="data-[state=checked]:bg-emerald-600" />
          </div>

          {/* Push */}
          <div className="flex items-center justify-between py-5 border-b border-slate-100 last:border-0">
            <div>
              <div className="text-[13px] font-semibold text-slate-800 mb-0.5">{t("Push Notifications")}</div>
              <div className="text-xs text-slate-500">{t("Browser push notifications")}</div>
            </div>
            <Switch checked={push} onCheckedChange={setPush} className="data-[state=checked]:bg-emerald-600" />
          </div>

          {/* Paperless */}
          <div className="flex items-center justify-between py-5 border-b border-slate-100 last:border-0 pb-0">
            <div>
              <div className="text-[13px] font-semibold text-slate-800 mb-0.5">{t("Paperless Billing")}</div>
              <div className="text-xs text-slate-500">{t("Receive bills digitally only")}</div>
            </div>
            <Switch checked={paperless} onCheckedChange={setPaperless} className="data-[state=checked]:bg-emerald-600" />
          </div>
        </div>
      </Card>

      {/* Language */}
      <Card className="border border-slate-200 shadow-sm rounded-2xl p-6 md:p-8 bg-white">
        <div className="flex items-center gap-2.5 mb-4 text-slate-800">
          <Globe className="w-[18px] h-[18px] text-blue-500" />
          <h3 className="text-[15px] font-bold">{t("Language")}</h3>
        </div>
        
        <p className="text-[13px] text-slate-500 mb-6">{t("Select the display language for the portal")}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* English */}
          <div 
            onClick={() => setLanguage('en')}
            className={`cursor-pointer rounded-xl p-4 border transition-all flex items-center justify-between ${
              language === 'en' 
                ? 'border-blue-500 bg-[#f8faff]' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-4">
              <span className={`text-[15px] font-bold ${language === 'en' ? 'text-slate-800' : 'text-slate-600'}`}>US</span>
              <div className="flex flex-col">
                <span className={`text-[13px] font-bold ${language === 'en' ? 'text-blue-700' : 'text-slate-800'}`}>{t("English")}</span>
                <span className="text-[11px] text-slate-400 font-medium">EN</span>
              </div>
            </div>
            {language === 'en' && <CheckCircle2 className="w-4 h-4 text-blue-500" />}
          </div>

          {/* French */}
          <div 
            onClick={() => setLanguage('fr')}
            className={`cursor-pointer rounded-xl p-4 border transition-all flex items-center justify-between ${
              language === 'fr' 
                ? 'border-blue-500 bg-[#f8faff]' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-4">
              <span className={`text-[15px] font-bold ${language === 'fr' ? 'text-slate-800' : 'text-slate-600'}`}>FR</span>
              <div className="flex flex-col">
                <span className={`text-[13px] font-bold ${language === 'fr' ? 'text-blue-700' : 'text-slate-800'}`}>{t("Français")}</span>
                <span className="text-[11px] text-slate-400 font-medium">FR</span>
              </div>
            </div>
            {language === 'fr' && <CheckCircle2 className="w-4 h-4 text-blue-500" />}
          </div>
        </div>
      </Card>

      <div className="mt-2">
        <Button onClick={handleSave} disabled={isPending} className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 rounded-lg px-6 h-[42px] text-[13px] font-bold">
          <Save className="w-4 h-4" /> {isPending ? "Saving..." : t("Save Preferences")}
        </Button>
      </div>
    </div>
  );
};
